import { gql, useMutation } from '@apollo/client'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import * as yup from 'yup'
import { fromLondonToUTCISOString, fromUTCISOStringToLondon } from '../../../helpers/date'
import { upload } from '../../../helpers/uploadFileToS3'
import { validationDate, validationString } from './Fields'
import { RecoveryCertificateQuery_recoveryCertificates } from './__generated__/RecoveryCertificateQuery'
import {
  updateRecoveryCertificateFormMutation,
  updateRecoveryCertificateFormMutationVariables,
} from './__generated__/updateRecoveryCertificateFormMutation'

const updateRecoveryCertificateMutation = gql`
  mutation updateRecoveryCertificateFormMutation($input: UpdateRecoveryCertificateInput!) {
    updateRecoveryCertificate(input: $input) {
      orderId
      status
      filePutUrls
      fileUrls
      certificateFile
      success
    }
  }
`
export type OnSubmitParams = {
  result: boolean
  action?: updateRecoveryCertificateFormMutationVariables['input']['action']
  status?: RecoveryCertificateQuery_recoveryCertificates['status']
  error?: Error
}

export type OnSubmit = ({ result, action, status, error }: OnSubmitParams) => void

export const UpdateRecoveryCertificateForm = ({
  recoveryCertificate,
  refetchRecoveryCertificate,
  children,
  onSubmit,
}: {
  recoveryCertificate: RecoveryCertificateQuery_recoveryCertificates
  refetchRecoveryCertificate: () => void
  children: React.ReactNode
  onSubmit: OnSubmit
}) => {
  const [updateRecoveryCertificate] = useMutation<
    updateRecoveryCertificateFormMutation,
    updateRecoveryCertificateFormMutationVariables
  >(updateRecoveryCertificateMutation)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const validationSchema = yup.object().shape({
    firstName: validationString('First Name'),
    lastName: validationString('Last Name'),
    dateOfBirth: validationDate('Date of Birth', 'dob'),
    emailAddress: validationString('Email Address'),
    passportNumber: validationString('Passport Number'),
    positiveCovidTestDate: validationDate('Positive Covid Test Date'),
  })

  const submitForm = async (data) => {
    try {
      if (data.typeOfCovidTest === 'NAAT_TMA_LAMP' && !(data.typeOfCovidTestDescription?.length > 0)) {
        throw new Error(
          'When "Type of Covid Test" is "Naat Tma Lamp" you must provide a "Type of Covid Test Description"'
        )
      }

      if (!isSubmitting) {
        setIsSubmitting(true)

        const fileUploads = []
        if (data.fileUploads) {
          Array.from(data.fileUploads).forEach((file) => {
            fileUploads.push({
              extension: `${file['name'].split('.').slice(-1)}`,
              contentType: file['type'],
            })
          })
        }

        const action = data.action || 'UPDATE'

        const {
          data: { updateRecoveryCertificate: updateRecoveryCertificateResult },
        } = await updateRecoveryCertificate({
          variables: {
            input: {
              orderId: recoveryCertificate.orderId,
              action,

              completingFor: data.completingFor,
              completingForFirstName: data.completingForFirstName,
              completingForLastName: data.completingForLastName,
              completingForRelationshipToPatient: data.completingForRelationshipToPatient,
              firstName: data.firstName,
              lastName: data.lastName,
              dateOfBirth: fromLondonToUTCISOString(data.dateOfBirth),
              emailAddress: data.emailAddress,
              telephoneNumber: data.telephoneNumber,
              passportNumber: data.passportNumber,

              travelToDestination: data.travelToDestination || undefined,
              travelToDestinationOtherDescription: data.travelToDestinationOtherDescription,

              positiveCovidTestDate: fromLondonToUTCISOString(data.positiveCovidTestDate),
              typeOfCovidTest: data.typeOfCovidTest,
              typeOfCovidTestDescription: data.typeOfCovidTestDescription || '',
              covidSymptomsAroundTestDate: data.covidSymptomsAroundTestDate === 'true',
              lastTimeExperiencedSymptoms: data.lastTimeExperiencedSymptoms,
              additionalInformation: data.additionalInformation || '',

              fileUrls: data.fileUrls,
              fileUploads,

              status: data.status,
              paymentStatus: data.paymentStatus,
              notes: data.notes || '',
            },
          },
        })

        // Upload Files
        const filePutUrls = updateRecoveryCertificateResult?.filePutUrls

        for (let index = 0; index < filePutUrls?.length; index++) {
          await upload({
            putUrl: filePutUrls[index],
            file: data.fileUploads[index],
          })
        }

        if (action === 'UPDATE') {
          refetchRecoveryCertificate()
        }

        if (action === 'PREVIEW') {
          const a = document.createElement('a')
          a.href = `/dashboard/recovery-certificate/${recoveryCertificate.orderId}?previewRecoveryCertificate=1`
          a.target = '_blank'
          a.click()

          refetchRecoveryCertificate()
        } else {
          onSubmit &&
            onSubmit({
              result: true,
              action,
              status: updateRecoveryCertificateResult?.status,
            })
        }

        setIsSubmitting(false)
      }
    } catch (e) {
      onSubmit &&
        onSubmit({
          result: false,
          error: e,
        })
      console.error(e)
      setIsSubmitting(false)
    }
  }

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={submitForm}
      initialValues={{
        ...recoveryCertificate,
        dateOfBirth: recoveryCertificate?.dateOfBirth && fromUTCISOStringToLondon(recoveryCertificate.dateOfBirth),
        positiveCovidTestDate:
          recoveryCertificate?.positiveCovidTestDate &&
          fromUTCISOStringToLondon(recoveryCertificate.positiveCovidTestDate),
      }}
    >
      <Form>{children}</Form>
    </Formik>
  )
}

export default UpdateRecoveryCertificateForm
